import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import { useDevPlanActions } from '@/hooks/useDevPlanActions';
import { Check, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { HabitFrequency } from '@/types/goal';
import {
  DAY_INITIALS,
  bitmaskFromDayIndices,
} from '@/lib/habit-schedule';

interface AddHabitFormProps {
  onSubmit: (title: string, frequency: HabitFrequency, scheduleDays: number | null, linkedActionId?: string) => void;
  onCancel: () => void;
}

type TopLevelChoice = 'daily' | 'weekly' | 'custom';

// Quick-select shortcuts shown inside the custom day picker
const CUSTOM_SHORTCUTS: { label: string; days: number[] }[] = [
  { label: 'Weekdays', days: [1, 2, 3, 4, 5] },
  { label: 'MWF', days: [1, 3, 5] },
  { label: 'Tue/Thu', days: [2, 4] },
  { label: 'Weekends', days: [0, 6] },
];

export const AddHabitForm = ({ onSubmit, onCancel }: AddHabitFormProps) => {
  const [title, setTitle] = useState('');
  const [choice, setChoice] = useState<TopLevelChoice>('daily');
  const [customDays, setCustomDays] = useState<number[]>([]); // day indices
  const [selectedAction, setSelectedAction] = useState<string>('none');
  const { milestones, hasPlan, loading, ensureActionRecord } = useDevPlanActions();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll the form into view and focus the input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, []);

  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getFrequencyAndDays = (): { frequency: HabitFrequency; scheduleDays: number | null } => {
    if (choice === 'daily') return { frequency: 'daily', scheduleDays: 127 };
    if (choice === 'weekly') return { frequency: 'weekly', scheduleDays: null };
    // custom
    if (customDays.length === 0) return { frequency: 'daily', scheduleDays: 127 };
    // Check if custom selection matches a known pattern
    const mask = bitmaskFromDayIndices(customDays);
    if (mask === 127) return { frequency: 'daily', scheduleDays: 127 };
    if (mask === 62) return { frequency: 'weekdays', scheduleDays: 62 };
    return { frequency: 'specific_days', scheduleDays: mask };
  };

  const handleSubmit = async () => {
    if (!title.trim()) return;

    setIsSubmitting(true);

    try {
      let linkedActionId: string | undefined;

      if (selectedAction !== 'none') {
        const [milestoneIndex, actionIndex] = selectedAction.split('-').map(Number);
        linkedActionId = await ensureActionRecord(milestoneIndex, actionIndex) || undefined;
      }

      const { frequency, scheduleDays } = getFrequencyAndDays();
      onSubmit(title.trim(), frequency, scheduleDays, linkedActionId);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleCustomDay = (dayIndex: number) => {
    setCustomDays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex].sort()
    );
  };

  const applyShortcut = (days: number[]) => {
    setCustomDays(days);
  };

  return (
    <div className="space-y-4 p-4 rounded-lg border border-border bg-card/50">
      {/* Habit Title */}
      <div className="space-y-2">
        <Label htmlFor="habit-title">Habit Title</Label>
        <Input
          ref={inputRef}
          id="habit-title"
          data-testid="habit-title-input"
          aria-label="New habit title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Read for 20 minutes"
          className="h-12"
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />
      </div>

      {/* Frequency Selection — Daily / Weekly / Custom */}
      <div className="space-y-2">
        <Label>Frequency</Label>
        <div className="flex flex-wrap gap-2">
          {(['daily', 'weekly', 'custom'] as const).map((opt) => (
            <Button
              key={opt}
              type="button"
              variant={choice === opt ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChoice(opt)}
              className="text-xs capitalize"
            >
              {opt}
            </Button>
          ))}
        </div>
      </div>

      {/* Custom Day Picker */}
      {choice === 'custom' && (
        <div className="space-y-3">
          <Label>Select Days</Label>
          <div className="flex gap-1.5">
            {DAY_INITIALS.map((initial, i) => (
              <button
                key={i}
                type="button"
                onClick={() => toggleCustomDay(i)}
                className={cn(
                  "w-9 h-9 min-w-[44px] min-h-[44px] rounded-full text-xs font-medium transition-colors border",
                  customDays.includes(i)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:border-primary/50"
                )}
              >
                {initial}
              </button>
            ))}
          </div>
          {/* Quick-select shortcuts */}
          <div className="flex flex-wrap gap-1.5">
            {CUSTOM_SHORTCUTS.map((shortcut) => {
              const isActive = shortcut.days.length === customDays.length &&
                shortcut.days.every(d => customDays.includes(d));
              return (
                <button
                  key={shortcut.label}
                  type="button"
                  onClick={() => applyShortcut(shortcut.days)}
                  className={cn(
                    "px-2.5 py-1 rounded-md text-xs transition-colors border",
                    isActive
                      ? "bg-primary/15 text-primary border-primary/30"
                      : "text-muted-foreground border-border hover:border-primary/50"
                  )}
                >
                  {shortcut.label}
                </button>
              );
            })}
          </div>
          {customDays.length === 0 && (
            <p className="text-xs text-muted-foreground">Select at least one day</p>
          )}
        </div>
      )}

      {/* Development Plan Link - Only show if user has a plan */}
      {hasPlan && !loading && milestones.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="habit-action-link" className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-muted-foreground" />
            Link to Development Plan (Optional)
          </Label>
          <Select value={selectedAction} onValueChange={setSelectedAction}>
            <SelectTrigger
              id="habit-action-link"
              className="h-12 bg-background border-border focus:ring-primary focus:border-primary"
            >
              <SelectValue placeholder="None - This is a standalone habit" />
            </SelectTrigger>
            <SelectContent
              className="bg-popover border-border max-h-[300px] overflow-y-auto z-50"
            >
              <SelectItem value="none" className="text-muted-foreground">
                None - This is a standalone habit
              </SelectItem>

              {milestones.map((milestone) => (
                <SelectGroup key={milestone.index}>
                  <SelectLabel className="font-bold text-muted-foreground px-2 py-1.5 text-xs uppercase tracking-wide">
                    {truncateText(milestone.title, 45)}
                  </SelectLabel>
                  {milestone.actions.map((action) => {
                    const isDisabled = action.completed || action.isLinkedToHabit;
                    const value = `${action.milestoneIndex}-${action.actionIndex}`;

                    return (
                      <SelectItem
                        key={value}
                        value={value}
                        disabled={isDisabled}
                        className={cn(
                          "pl-6",
                          isDisabled && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <span className="flex items-center gap-2">
                          {action.completed && (
                            <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          )}
                          <span className={cn(
                            action.completed && "line-through text-muted-foreground"
                          )}>
                            {truncateText(action.actionText)}
                          </span>
                          {action.isLinkedToHabit && !action.completed && (
                            <span className="text-xs text-muted-foreground">(linked)</span>
                          )}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Connect this habit to a specific action in your plan to track progress automatically
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 pt-2">
        <Button
          onClick={handleSubmit}
          size="sm"
          disabled={!title.trim() || isSubmitting || (choice === 'custom' && customDays.length === 0)}
        >
          {isSubmitting ? 'Adding...' : 'Add Habit'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
